import moment from "moment-timezone"
import { ISwimDatum } from "./parseSd3"

// ─────────────────────────────────────────────────────────────────────────────
// HY3 fixed-width results parser
// Record length: 130 chars + CRLF  (all positions are 0-indexed)
//
// Record types parsed:
//   C1  – club / team header
//   D1  – individual swimmer definition
//   E1  – individual event header (one per swimmer-round)
//   E2  – individual event result  (F = Finals, P = Prelims)
//   F1  – relay team entry header
//   F2  – relay team result        (F = Finals, P = Prelims)
//   F3  – relay leg swimmers       (all 4 legs packed into one record)
// ─────────────────────────────────────────────────────────────────────────────

export interface IHy3ParseOptions {
  /**
   * "finals"  = only E2F/F2F rows emitted
   * "prelims" = only E2P/F2P rows emitted
   * "both"    = all rounds emitted, each as its own entry with round field set
   */
  importScope: "finals" | "prelims" | "both"
  /**
   * true  → use event number from E1[38:41] as eventNumber (default, recommended)
   * false → set eventNumber = -1 so server falls back to dist+stroke+age matching
   */
  matchOnEventNumbers: boolean
}

// ─── Stroke maps ─────────────────────────────────────────────────────────────

const HY3_STROKE_MAP: Record<string, string> = {
  A: "Freestyle",
  B: "Backstroke",
  C: "Breaststroke",
  D: "Butterfly",
}

const HY3_RELAY_STROKE_MAP: Record<string, string> = {
  A: "Freestyle Relay",
  E: "Medley Relay",
  // fall-through: treat unknown relay strokes as Freestyle Relay
}

function mapIndividualStroke(code: string): string {
  return HY3_STROKE_MAP[code.toUpperCase()] ?? "Freestyle"
}

function mapRelayStroke(code: string): string {
  return HY3_RELAY_STROKE_MAP[code.toUpperCase()] ?? "Freestyle Relay"
}

// ─── Time formatting ─────────────────────────────────────────────────────────

/**
 * Parse a HY3 time value string (e.g. "27.92", "165.27") into the decimal-
 * seconds string that the server expects (same as parseSd3's formatTime).
 * Returns "DQ" | "NS" for those statuses, or the numeric string otherwise.
 */
function formatHy3Time(rawTime: string, statusCode: string): string {
  if (statusCode === "R") return "NS" // No Swim
  if (statusCode === "Q") return "DQ" // Disqualified

  const t = rawTime.trim()
  if (!t || t === "0.00" || t === "0") return ""

  try {
    // Parse "SS.hh" or "M:SS.hh" format
    let hundredths = 0
    let workTime = t
    if (/\./.test(workTime)) {
      hundredths = Math.floor(
        parseFloat("." + workTime.split(".")[1]) * 100,
      )
      workTime = workTime.split(".")[0]
    }
    const parts = workTime.split(":")
    let seconds = 0
    for (let i = parts.length - 1; i >= 0; i--) {
      seconds += 60 ** (parts.length - i - 1) * parseInt(parts[i], 10)
    }
    return String(seconds + hundredths / 100)
  } catch {
    return ""
  }
}

// ─── Age group helpers ────────────────────────────────────────────────────────

/**
 * Build the 4-char eventAge string used by findSwimEventId:
 *   "UN06" → 6&Under   "0910" → 9-10   "1517" → 15-17   "18OV" → 18&Over
 */
function buildEventAge(minRaw: string, maxRaw: string): string {
  const min = parseInt(minRaw.trim(), 10)
  const max = parseInt(maxRaw.trim(), 10)

  if (isNaN(min) || isNaN(max)) return "UNOV"
  if (min === 0 && max === 0) return "UNOV"
  // Hy-Tek uses 109 as "no upper bound" (open/over)
  if (max >= 99) return `${String(min).padStart(2, "0")}OV`
  if (min === 0) return `UN${String(max).padStart(2, "0")}`
  return `${String(min).padStart(2, "0")}${String(max).padStart(2, "0")}`
}

// ─── D1 swimmer cache ─────────────────────────────────────────────────────────

interface IHy3Swimmer {
  fileId: string
  gender: string // "M" | "F"
  lastName: string
  firstName: string
  dob: string // "YYYY-MM-DD"
  usasId: string // may be empty
}

// ─── Blank entry factories (mirroring parseSd3 defaults) ─────────────────────

function newIndividualEntry() {
  return {
    swimmerTeamPosition: 1,
    relayOnlyRecord: "0",
    isRelay: "0",
    relayTeam: 1,
    teamPosition: 1,
    swimmerLastName: "",
    swimmerFirstName: "",
    swimmerUSAId: "",
    swimmerDOB: "",
    swimmerGender: "",
    eventGender: "",
    eventStroke: "",
    eventDistance: -1,
    eventNumber: -1,
    eventAge: "",
    seedTime: "",
    seedTimeCourse: "",
    prelimTime: "",
    prelimTimeCourse: "",
    swimOffTime: "",
    swimOffTimeCourse: "",
    finalsTime: "",
    finalsTimeCourse: "",
    prelimHeat: "",
    prelimLane: "",
    prelimPlace: "",
    finalsHeat: "",
    finalsLane: "",
    finalsPlace: "",
    finalsPoints: "",
    isExhibition: "0",
    alternateDesignation: "",
    round: null as "prelims" | "finals" | "timedFinals" | null,
  }
}

function newRelayEntry() {
  return {
    relayOnlyRecord: "0",
    isRelay: "1",
    relayTeam: 1,
    eventGender: "",
    eventStroke: "",
    eventDistance: -1,
    eventNumber: -1,
    eventAge: "",
    seedTime: "",
    seedTimeCourse: "",
    prelimTime: "",
    prelimTimeCourse: "",
    swimOffTime: "",
    swimOffTimeCourse: "",
    finalsTime: "",
    finalsTimeCourse: "",
    prelimHeat: "",
    prelimLane: "",
    prelimPlace: "",
    finalsHeat: "",
    finalsLane: "",
    finalsPlace: "",
    finalsPoints: "",
    isExhibition: "0",
    alternateDesignation: "",
    round: null as "prelims" | "finals" | "timedFinals" | null,
    relayLegs: [] as {
      swimmerLastName: string
      swimmerFirstName: string
      swimmerUSAId: string
      swimmerGender: string
      swimmerDOB: string
      teamPosition: number
    }[],
  }
}

function newSd3Club() {
  return {
    teamCode: "",
    teamFullName: "",
    teamShortName: "",
    teamCity: "",
    teamState: "",
    teamZipCode: "",
    individualEntries: [] as ReturnType<typeof newIndividualEntry>[],
    relayEntries: [] as ReturnType<typeof newRelayEntry>[],
  }
}

// ─── Main parser ──────────────────────────────────────────────────────────────

/**
 * Parse a HY3 binary-style text file (CRLF-delimited 130-char records) and
 * return an ISwimDatum compatible with the existing SD3/CL2 import pipeline.
 */
export function parseHy3Results(
  fileContents: string,
  options: IHy3ParseOptions,
): ISwimDatum {
  const { importScope, matchOnEventNumbers } = options

  const swimDatum: ISwimDatum = {
    clubs: [],
    orgCode: "",
    filePurpose: "Meet Results",
    meetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    meetDate: "",
  }

  // Split on CRLF or bare LF; ignore empty / short lines
  const lines = fileContents
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((l) => l.length >= 2)

  // ── State ──────────────────────────────────────────────────────────────────
  const swimmerMap = new Map<string, IHy3Swimmer>()

  let currentClub: ReturnType<typeof newSd3Club> | null = null
  // Each E1 builds a fresh pendingEntry; E2 finalizes it and pushes to currentClub
  let currentE1: {
    fileId: string
    eventNumber: number
    pendingEntry: ReturnType<typeof newIndividualEntry>
  } | null = null
  // Each F1 builds a fresh pendingEntry; F2 finalizes it and pushes to currentClub
  let currentF1: {
    teamCode: string
    relayLetter: string
    eventNumber: number
    pendingEntry: ReturnType<typeof newRelayEntry>
  } | null = null

  for (const line of lines) {
    const type = line.slice(0, 2)

    // ── C1 – Club header ────────────────────────────────────────────────────
    if (type === "C1") {
      currentClub = newSd3Club()
      currentClub.teamCode = line.slice(2, 6).trim()
      currentClub.teamFullName = line.slice(7, 31).trim()
      currentClub.teamShortName = line.slice(32, 50).trim()
      swimDatum.clubs.push(currentClub)
      continue
    }

    // ── D1 – Swimmer definition ──────────────────────────────────────────────
    if (type === "D1") {
      const gender = line[2] // "M" | "F"
      const fileId = line.slice(3, 8)
      const lastName = line.slice(8, 28).trim()
      const firstName = line.slice(28, 48).trim()
      const usasIdRaw = line.slice(68, 84).trim()
      const dobRaw = line.slice(88, 96) // MMDDYYYY
      const dobFormatted = moment(dobRaw, "MMDDYYYY").format("YYYY-MM-DD")
      swimmerMap.set(fileId, {
        fileId,
        gender,
        lastName,
        firstName,
        dob: dobFormatted === "Invalid date" ? "" : dobFormatted,
        usasId: usasIdRaw,
      })
      continue
    }

    // ── B1/B2 – Meet info ────────────────────────────────────────────────────
    if (type === "B1") {
      // start date is in the B1 record (approximate position; HY3 meet info)
      const startDateRaw = line.slice(25, 33).trim()
      if (startDateRaw.length === 8) {
        swimDatum.meetDate = moment(startDateRaw, "MMDDYYYY").format(
          "YYYY-MM-DD",
        )
      }
      continue
    }

    // ── E1 – Individual event header ─────────────────────────────────────────
    // Each E1 always creates a fresh pendingEntry; E2 finalizes and pushes it.
    // This means a swimmer in both prelims and finals emits two separate entries.
    if (type === "E1") {
      const fileId = line.slice(3, 8)
      const distanceRaw = line.slice(15, 21).trim()
      const strokeCode = line[21] // A/B/C/D
      const ageMinRaw = line.slice(23, 25)
      const ageMaxRaw = line.slice(26, 28)
      const entryType = line.slice(30, 32) // "0S" | "0A"
      const eventNumberRaw = line.slice(38, 41).trim()
      // E1[61:65] = per-swimmer earned points (awarded in finals)
      const pointsRaw = line.slice(61, 65).trim()

      const isAlternate = entryType === "0A"
      const eventNumber = matchOnEventNumbers
        ? parseInt(eventNumberRaw, 10) || -1
        : -1
      const eventAge = buildEventAge(ageMinRaw, ageMaxRaw)

      const swimmer = swimmerMap.get(fileId)
      if (!swimmer || !currentClub) {
        currentE1 = null
        continue
      }

      const entry = newIndividualEntry()
      entry.swimmerLastName = swimmer.lastName
      entry.swimmerFirstName = swimmer.firstName
      entry.swimmerGender = swimmer.gender
      entry.swimmerDOB = swimmer.dob
      entry.swimmerUSAId = swimmer.usasId
      entry.eventGender = swimmer.gender
      entry.eventStroke = mapIndividualStroke(strokeCode)
      entry.eventDistance = parseInt(distanceRaw, 10) || -1
      entry.eventNumber = eventNumber
      entry.eventAge = eventAge
      entry.isExhibition = isAlternate ? "1" : "0"
      entry.alternateDesignation = isAlternate ? "A" : ""
      entry.finalsPoints = pointsRaw
      const seedRaw = line.slice(45, 51).trim().replace(/Y$/, "")
      entry.seedTime = seedRaw
      entry.seedTimeCourse = "Y"

      currentE1 = { fileId, eventNumber, pendingEntry: entry }
      continue
    }

    // ── E2 – Individual event result ─────────────────────────────────────────
    if (type === "E2") {
      if (!currentE1 || !currentClub) continue

      const e2Round = line[2] as "F" | "P"

      // Apply importScope filter
      if (e2Round === "P" && importScope === "finals") continue
      if (e2Round === "F" && importScope === "prelims") continue

      const timeRaw = line.slice(3, 11).trim()
      const status = line[12] // " " | "R" (NS) | "Q" (DQ)
      const formattedTime = formatHy3Time(timeRaw, status)

      // E2[20:23]=heat  [23:26]=lane  [26:29]=place_in_heat  [29:33]=place_in_event
      const heat = line.slice(20, 23).trim()
      const lane = line.slice(23, 26).trim()
      const placeInEvent = line.slice(29, 33).trim()

      const entry = currentE1.pendingEntry
      if (e2Round === "F") {
        entry.finalsTime = formattedTime
        entry.finalsTimeCourse = "Y"
        entry.finalsHeat = heat
        entry.finalsLane = lane
        entry.finalsPlace = placeInEvent
        entry.round = "finals"
      } else {
        entry.prelimTime = formattedTime
        entry.prelimTimeCourse = "Y"
        entry.prelimHeat = heat
        entry.prelimLane = lane
        entry.prelimPlace = placeInEvent
        entry.finalsPoints = ""  // points belong to the finals entry
        entry.round = "prelims"
      }

      currentClub.individualEntries.push(entry)
      continue
    }

    // ── F1 – Relay team entry header ─────────────────────────────────────────
    // Each F1 creates a fresh pendingEntry; F2 finalizes and pushes it.
    if (type === "F1") {
      if (!currentClub) continue
      const teamCode = line.slice(2, 6).trim()
      const relayLetter = line[7].trim() // A, B, C …
      const distanceRaw = line.slice(15, 21).trim()
      const strokeCode = line[21] // A (FreRelay) | E (MedRelay)
      const ageMinRaw = line.slice(23, 25)
      const ageMaxRaw = line.slice(26, 28)
      const entryType = line.slice(30, 32)
      const eventNumberRaw = line.slice(38, 41).trim()

      const isAlternate = entryType === "0A"
      const eventNumber = matchOnEventNumbers
        ? parseInt(eventNumberRaw, 10) || -1
        : -1

      const entry = newRelayEntry()
      entry.eventGender = line[12] === "F" ? "F" : "M"
      entry.eventStroke = mapRelayStroke(strokeCode)
      entry.eventDistance = parseInt(distanceRaw, 10) || -1
      entry.eventNumber = eventNumber
      entry.eventAge = buildEventAge(ageMinRaw, ageMaxRaw)
      entry.isExhibition = isAlternate ? "1" : "0"
      entry.alternateDesignation = isAlternate ? "A" : ""
      entry.relayTeam = relayLetter.charCodeAt(0) - 64 // A→1
      const seedRaw = line.slice(45, 51).trim().replace(/Y$/, "")
      entry.seedTime = seedRaw
      entry.seedTimeCourse = "Y"

      currentF1 = { teamCode, relayLetter, eventNumber, pendingEntry: entry }
      continue
    }

    // ── F2 – Relay team result ───────────────────────────────────────────────
    if (type === "F2") {
      if (!currentF1 || !currentClub) continue

      const f2Round = line[2] as "F" | "P"

      // Apply importScope filter
      if (f2Round === "P" && importScope === "finals") continue
      if (f2Round === "F" && importScope === "prelims") continue

      const timeRaw = line.slice(3, 11).trim()
      const status = line[12]
      const formattedTime = formatHy3Time(timeRaw, status)

      const heat = line.slice(20, 23).trim()
      const lane = line.slice(23, 26).trim()
      const placeInEvent = line.slice(29, 33).trim()

      const entry = currentF1.pendingEntry
      if (f2Round === "F") {
        entry.finalsTime = formattedTime
        entry.finalsTimeCourse = "Y"
        entry.finalsHeat = heat
        entry.finalsLane = lane
        entry.finalsPlace = placeInEvent
        entry.round = "finals"
      } else {
        entry.prelimTime = formattedTime
        entry.prelimTimeCourse = "Y"
        entry.prelimHeat = heat
        entry.prelimLane = lane
        entry.prelimPlace = placeInEvent
        entry.round = "prelims"
      }

      currentClub.relayEntries.push(entry)
      continue
    }

    // ── F3 – Relay leg swimmers ──────────────────────────────────────────────
    // Legs are attached to the most recent F1's pendingEntry (by reference,
    // so they remain correct whether F3 appears before or after F2).
    if (type === "F3") {
      if (!currentF1) continue
      const entry = currentF1.pendingEntry

      const LEG_SIZE = 11
      const SEPARATOR_SIZE = 2
      const LEGS_START = 2
      for (let leg = 0; leg < 4; leg++) {
        const offset = LEGS_START + leg * (LEG_SIZE + SEPARATOR_SIZE)
        if (offset + LEG_SIZE > line.length) break

        const legFileId = line.slice(offset + 1, offset + 6)
        const swimmer = swimmerMap.get(legFileId)

        if (swimmer) {
          entry.relayLegs.push({
            swimmerLastName: swimmer.lastName,
            swimmerFirstName: swimmer.firstName,
            swimmerUSAId: swimmer.usasId,
            swimmerGender: swimmer.gender,
            swimmerDOB: swimmer.dob,
            teamPosition: leg + 1,
          })
        }
      }
      continue
    }
  }

  return swimDatum
}

// ─── Unique-swimmer extraction (mirroring parseSd3's findUniqueSwimmers) ──────

export interface IHy3SwimmerInFile {
  club: string
  swimmerFirstName: string
  swimmerLastName: string
  swimmerGender: string
  swimmerDOB: string
  swimmerUSAId: string
  swimmerId: number
}

export function findUniqueHy3Swimmers(swimData: ISwimDatum): IHy3SwimmerInFile[] {
  const seen = new Set<string>()
  const out: IHy3SwimmerInFile[] = []

  for (const club of swimData.clubs) {
    for (const entry of club.individualEntries) {
      const key = `${club.teamFullName}|${entry.swimmerFirstName}|${entry.swimmerLastName}|${entry.swimmerGender}|${entry.swimmerDOB}`
      if (!seen.has(key)) {
        seen.add(key)
        out.push({
          club: club.teamFullName,
          swimmerFirstName: entry.swimmerFirstName,
          swimmerLastName: entry.swimmerLastName,
          swimmerGender: entry.swimmerGender,
          swimmerDOB: entry.swimmerDOB,
          swimmerUSAId: (entry as any).swimmerUSAId ?? "",
          swimmerId: -1,
        })
      }
    }
    for (const relayEntry of club.relayEntries) {
      for (const leg of relayEntry.relayLegs) {
        const key = `${club.teamFullName}|${leg.swimmerFirstName}|${leg.swimmerLastName}|${leg.swimmerGender}|${leg.swimmerDOB}`
        if (!seen.has(key)) {
          seen.add(key)
          out.push({
            club: club.teamFullName,
            swimmerFirstName: leg.swimmerFirstName,
            swimmerLastName: leg.swimmerLastName,
            swimmerGender: leg.swimmerGender,
            swimmerDOB: leg.swimmerDOB,
            swimmerUSAId: leg.swimmerUSAId,
            swimmerId: -1,
          })
        }
      }
    }
  }

  return out
}
